import { Card, Form, Row, Col, Select, DatePicker, Button, Popover } from "antd";
import { useTranslation } from "react-i18next";
import { Fragment, useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useAppSelector } from "store/hook";
import { useListCustomerInf } from "Components/Management/queries";
import { useDeviceStatus } from "Components/Dashboard/queries";
import { Info } from "Utility/Types/management";
import { useFilter } from "Utility/hooks/useFilter";
import { TableData } from "Utility/Types/status_identity_get_set";
import { setFilterForm } from "Components/Report/reportSlice";
import { useAppDispatch } from "store/hook";
import SelectorFooter from "Components/Shared/SelectorFooter";
import type { RangePickerProps } from "antd/es/date-picker";
import type { Moment } from "moment";
import moment from "moment";
import { useMDCollapse } from "Utility/hooks/mdCollapse";
import { DownOutlined, UpOutlined, FilterOutlined } from "@ant-design/icons";
import FiledFilter from "Components/Shared/FieldFilter";
import { setFields } from "Components/Topology/Setting/LTE/setFields";

const { RangePicker } = DatePicker;

const { Option } = Select;

export const periodicOptions = ["daily", "weekly", "monthly"] as const;

export const periodicReportDayOptions = {
  daily: [1],
  weekly: Array.from({ length: 7 }).map((_, index) => index + 1),
  monthly: Array.from({ length: 30 }).map((_, index) => index + 1),
};

export const periodicReportHourOptions = Array.from({ length: 24 }).map((_, index) => index);

export const timeOptions = [
  { label: "Today", value: 86400 },
  { label: "Last Day", value: 86400 },
  { label: "Last Week", value: 604800 },
  { label: "Last Month", value: 2592000 },
  { label: "Custom", value: "custom" },
] as const;

export const unitOptions = {
  86400: [{ label: "Hour", value: 3600 }],
  604800: [{ label: "Day", value: 86400 }],
  2592000: [
    { label: "Day", value: 86400 },
    { label: "Week", value: 604800 },
  ],
  2592001: [
    { label: "Day", value: 86400 },
    { label: "Week", value: 604800 },
    { label: "Month", value: 2592000 },
  ], //over 1 month
  custom: [
    { label: "Hour", value: 3600 },
    { label: "Day", value: 86400 },
    { label: "Week", value: 604800 },
    // { label: "Month", value: 2592000 },
  ],
};
// { label: "Hour", value: 3600 }, // 60* 60 = 3600
// { label: "Day", value: 86400 }, //24*60*60= 86400
// { label: "Week", value: 604800 }, //7*24*60*60 = 604800
// { label: "Month", value: 2592000 }} //30*24*60*60 = 2592000
//  as const;

export const accountOptions = [
  { label: "Health", value: "account_health" },
  { label: "Signal", value: "account_signal" },
  // { label: "Alarm Ratio", value: "account_alarmratio" },
  { label: "Health(By Rank)", value: "account_health_table" },
  { label: "Signal(By Rank)", value: "account_signal_table" },
  { label: "Data-Usage-EWAN(By Rank)", value: "account_usage_ewan_table" },
  { label: "Data-Usage-Cellular(By Rank)", value: "account_usage_cellular_table" },
  // { label: "Uptime(Top5)", value: "account_uptime_top5" },
  // { label: "All in One", value: "account_all_in_one" },
  // { label: "LAN Usage", value: "account_lan_usage" },
  // { label: "WAN Usage", value: "account_wan_usage" },
  // { label: "Celluar Usage", value: "account_celluar_usage" },
  // { label: "Alarm Uptime", value: "account_alarm_uptime" },

  // { label: "Signal(RSSI)", value: "account_rssi" },
] as const;

export const deviceOptions = [
  { label: "Health", value: "device_health" },
  { label: "Signal", value: "device_signal" },
  // { label: "Uptime-EWAN", value: "device_uptime_wan" },
  // { label: "Uptime-Celluar", value: "device_uptime_celluar" },
  // { label: "Uptime-System", value: "device_uptime" },

  // { label: "Signal(RSSI)", value: "device_rssi" },

  // { label: "LAN Usage", value: "device_lan_usage" },
  { label: "Data Usage-EWAN", value: "device_wan_usage" }, //with RSSI RSRP
  { label: "Data Usage-Celluar", value: "device_celluar_usage" }, //with RSSI RSRP
  // { label: "Alarm Uptime", value: "device_alarm_uptime" },
] as const;

export type AccountValues = (typeof accountOptions)[number]["value"];
export type DeviceValues = (typeof deviceOptions)[number]["value"];

const initialFilter = {
  model: [],
  groups: [],
  city: [],
};

type RangeValue = [Moment | null, Moment | null] | null;

//to do: solve re-render&re-fetch issue if putting currentTime into component
let dateNow = Math.round(Date.now() / 1000);

const OnDemandFilter = () => {
  const { level, stateCid, isLogin, loginCid } = useAppSelector((status) => status.global);
  // const { filterForm } = useAppSelector((status) => status.report);
  const { isCollapse, setIsCollapse, isMD } = useMDCollapse();

  const isSuper = level === "super_super" && stateCid === "";
  const disp = useAppDispatch();
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const [selectedCid, setSelectedCid] = useState<any>("");
  const [accountFilterVisible, setAccountFilterVisible] = useState(false);
  const [deviceFilterVisible, setDeviceFilterVisible] = useState(false);
  const [company, setCompany] = useState<any>("");
  const [accountFilter, setAccountFilter] = useState<Record<string, string[]>>({ ...initialFilter });
  const [deviceFilter, setDeviceFilter] = useState<Record<string, string[]>>({ ...initialFilter });
  const [showPeriod, setShowPeriod] = useState<string | number>("");
  const { data: CustomerInfo } = useListCustomerInf(level, stateCid, isLogin);
  const { data: deviceStatus } = useDeviceStatus(level, stateCid, loginCid);
  const tableData = deviceStatus?.response?.tableData || [];
  const [accountTableData, setAccountTableData] = useState<Record<keyof TableData, any>[]>([]);
  const [deviceTableData, setDeviceTableData] = useState<Record<keyof TableData, any>[]>([]);
  const [time, setTime] = useState<any>(unitOptions[timeOptions[0].value]);
  const [unit, setUnit] = useState<string | number>(unitOptions[timeOptions[0].value][0].value);
  const [dates, setDates] = useState<RangeValue>(null);
  const [hackValue, setHackValue] = useState<RangeValue>(null);
  const [value, setValue] = useState<RangeValue>(null);
  const [currentTime, setCurrentTime] = useState(dateNow);
  const [isInputting, setIsInputting] = useState(false);
  useFilter({
    deviceStatus,
    filter: accountFilter,
    // tableData: accountTableData,
    tableData,
    setDeviceTableData: setAccountTableData,
  });

  useFilter({
    deviceStatus,
    filter: deviceFilter,
    tableData,
    setDeviceTableData: setDeviceTableData,
  });

  const onOpenChange = (open: boolean) => {
    if (open) {
      setHackValue([null, null]);
      setDates([null, null]);
    } else {
      setHackValue(null);
    }
  };

  const disabledStartDate: RangePickerProps["disabledDate"] = (current: Moment) => {
    if (!dates) {
      return false;
    }
    // const tooLate = (dates[0] && current.diff(dates[0], "days") === 0) as boolean;
    const tooEarly = current && current < moment().subtract(6, "months");
    // Can not select days before today and today
    // return tooEarly || tooLate;
    return tooEarly;
  };

  const handleTimeChange = (value: keyof typeof unitOptions) => {
    setTime(unitOptions[value]);
    setUnit(unitOptions[value][0].value); //set first as default
    form.setFieldsValue({ unit: unitOptions[value][0].value });

    if (value === "custom") {
      const data = form.getFieldValue("duration");
      dateOnChange(data);
    }
  };

  const onUnitChange = (value: keyof typeof unitOptions) => {
    setUnit(value);
  };

  // useEffect(() => {
  //   setAccountTableData(deviceStatus?.response?.tableData || []);
  //   setDeviceTableData(deviceStatus?.response?.tableData || []);
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [deviceStatus?.response?.tableData]);

  // ********************** Filter account-level 初始值 START **********************
  // useEffect(() => {
  //   if (!accountTableData.length) return;

  //   if (company === "" && isSuper) {
  //     return;
  //   } else {
  //     // 自動填滿 account-level devices
  //     form.setFieldsValue({
  //       account_device: accountTableData.map((o) => o.id),
  //     });
  //   }
  // }, [accountTableData]);

  useEffect(() => {
    // header 切換不同 customer, account level device 顯示全選該客戶的 device
    setCompany(stateCid);
  }, [stateCid]);
  // ********************** Filter account-level 初始值 END **********************

  const onFinish = (value: any) => {
    // dateOnChange(value.duration);
    setSelectedCid(value.company);
    disp(
      setFilterForm({
        ...value,
        start_utc: value["duration"]
          ? Math.round(value["duration"][0]?.startOf("day")?.format("X"))
          : currentTime - value.time,
        end_utc: value["duration"] ? Math.round(value["duration"][1]?.endOf("day")?.format("X")) : currentTime,
      })
    );
  };

  // { label: "Hour", value: 3600 },
  // { label: "Day", value: 86400 },
  // { label: "Week", value: 604800 },
  // { label: "Month", value: 2592000 },

  // { label: "Last Day", value: 86400 },
  // { label: "Last Week", value: 604800 },
  // { label: "Last Month", value: 2592000 },
  // { label: "Custom", value: "custom" },

  //when custom time rage changes, unit sould constrain by accordingly
  const dateOnChange = (value: any) => {
    setValue(value);

    const diffDays = (value?.[1]?.endOf("day")?.diff(value?.[0]?.startOf("day")) + 1) / 86400000;

    // 1 天顯示小時
    // 2 ~ 6 天顯示日
    // 7 ~ 29 天顯示日、周
    // 30 天以上顯示日、周、月

    if (diffDays === 1) {
      setTime(unitOptions["86400"]);
      setUnit(3600);
      form.setFieldsValue({ unit: 3600 });
    } else if (1 < diffDays && diffDays <= 6) {
      setTime(unitOptions["604800"]);
      setUnit(86400);
      form.setFieldsValue({ unit: 86400 });
    } else if (6 < diffDays && diffDays <= 29) {
      setTime(unitOptions["2592000"]);
      setUnit(86400);
      form.setFieldsValue({ unit: 86400 });
    } else if (29 < diffDays) {
      //over 1 month, unit can be week or month
      setTime(unitOptions["2592001"]);
      //set default unit to be week
      setUnit(604800);
      form.setFieldsValue({ unit: 604800 });
    } else {
      // when user clear duration, unit will set to day
      setTime(unitOptions["604800"]);
      setUnit(86400);
      form.setFieldsValue({ unit: 86400 });
    }
  };

  // 路由 /report 的 Filter (篩選組件)
  return (
    <Card
      title={isMD ? "Filter" : null}
      extra={
        isMD && (
          <Button
            type="text"
            icon={!isCollapse ? <UpOutlined /> : <DownOutlined />}
            onClick={() => setIsCollapse((prev) => !prev)}
          ></Button>
        )
      }
      bodyStyle={isCollapse ? { display: "none" } : {}}
    >
      <Form form={form} onFinish={onFinish} layout="vertical">
        <div className="formwrap">
          <Row gutter={24} justify="start" align="middle">
            {isSuper && (
              <Col xs={24} sm={24} md={12} lg={12} xl={6} xxl={4}>
                <Form.Item
                  name={"company"}
                  label="Customer"
                  initialValue={[]}
                  rules={[{ required: true, message: "required!" }]}
                >
                  {/* <Select maxTagCount="responsive" onChange={setSelectedCid}> */}
                  <Select
                    maxTagCount="responsive"
                    onChange={(value) => {
                      // FIXME:
                      if (isSuper && value !== "") {
                        //filter devices based on selected Cid
                        setAccountFilter((prev) => ({ cid: [value] }));
                        setDeviceFilter((prev) => ({ cid: [value] }));
                        setSelectedCid(value);
                        setCompany(value);

                        form.resetFields(["account_device", "device_device"]);
                      }
                    }}
                  >
                    {CustomerInfo?.response?.map((cust: Info) => (
                      <Option key={cust?.cid} value={cust?.cid}>
                        {cust?.inf_list?.company}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}
            <Col xs={24} sm={24} md={12} lg={12} xl={6} xxl={4}>
              <Form.Item
                name={"account_device"}
                label={
                  <Fragment>
                    Device(Account-Level)
                    <Popover
                      content={
                        <FiledFilter selectedCid={selectedCid} filter={accountFilter} setFilter={setAccountFilter} />
                      }
                      trigger="click"
                      visible={accountFilterVisible}
                      onVisibleChange={(visible) => setAccountFilterVisible(visible)}
                    >
                      <Button size="small" type="text" icon={<FilterOutlined />}></Button>
                    </Popover>
                  </Fragment>
                }
                // initialValue={accountTableData?.map((o) => o.id)}
                rules={[{ required: true, message: "required!" }]}
              >
                <Select
                  placeholder="Select"
                  mode="multiple"
                  allowClear
                  autoClearSearchValue={false}
                  optionFilterProp="label"
                  maxTagCount="responsive"
                  onSearch={(value: string) => {
                    value.length > 0 ? setIsInputting(true) : setIsInputting(false);
                  }}
                  onClear={() => {
                    setIsInputting(false);
                  }}
                  onFocus={() => {
                    setIsInputting(false);
                  }}
                  dropdownRender={(menu) =>
                    SelectorFooter({
                      menu,
                      selectAll: () =>
                        form.setFieldsValue({
                          account_device: accountTableData.map((o) => o.id),
                        }),
                      clearAll: () =>
                        form.setFieldsValue({
                          account_device: [],
                        }),
                      isInputting,
                    })
                  }
                >
                  {accountTableData.map((data) => (
                    <Option key={data.name_or_id} value={data.id} label={data.name_or_id}>
                      {data.name_or_id}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={12} lg={12} xl={6} xxl={4}>
              <Form.Item
                name={"account_parameter"}
                label="Parameter(Account-Level)"
                initialValue={accountOptions.map((p) => p.value)}
                rules={[{ required: true, message: "required!" }]}
              >
                <Select
                  placeholder="Select"
                  mode="multiple"
                  maxTagCount="responsive"
                  dropdownRender={(menu) =>
                    SelectorFooter({
                      menu,
                      selectAll: () =>
                        form.setFieldsValue({
                          account_parameter: accountOptions.map((o) => o.value),
                        }),
                      clearAll: () =>
                        form.setFieldsValue({
                          account_parameter: [],
                        }),
                    })
                  }
                >
                  {accountOptions.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Fragment>
              <Col xs={24} sm={24} md={12} lg={12} xl={6} xxl={4}>
                <Form.Item
                  name={"device_device"}
                  initialValue={[]}
                  label={
                    <Fragment>
                      Device(Device-Level)
                      <Popover
                        content={
                          <FiledFilter selectedCid={selectedCid} filter={deviceFilter} setFilter={setDeviceFilter} />
                        }
                        trigger="click"
                        visible={deviceFilterVisible}
                        onVisibleChange={(visible) => setDeviceFilterVisible(visible)}
                      >
                        <Button size="small" type="text" icon={<FilterOutlined />}></Button>
                      </Popover>
                    </Fragment>
                  }
                  rules={[
                    {
                      validator: async (_, value) => {
                        return value.length > 10 && Promise.reject(new Error("Max: 10"));
                      },
                    },
                  ]}
                >
                  <Select
                    placeholder="Select"
                    mode="multiple"
                    allowClear
                    autoClearSearchValue={false}
                    optionFilterProp="label"
                    maxTagCount="responsive"
                    onSearch={(value: string) => {
                      value.length > 0 ? setIsInputting(true) : setIsInputting(false);
                    }}
                    onClear={() => {
                      setIsInputting(false);
                    }}
                    onFocus={() => {
                      setIsInputting(false);
                    }}
                    dropdownRender={(menu) =>
                      SelectorFooter({
                        menu,
                        selectAll: () => {
                          form.setFieldsValue({
                            device_device: accountTableData.map((o) => o.id),
                          });
                          form.validateFields(["device_device"]); // 重新驗證
                        },
                        clearAll: () =>
                          form.setFieldsValue({
                            device_device: [],
                          }),
                        isInputting,
                      })
                    }
                  >
                    {deviceTableData.map((data) => (
                      <Option key={data.name_or_id} value={data.id} label={data.name_or_id}>
                        {data.name_or_id}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} sm={24} md={12} lg={12} xl={6} xxl={4}>
                <Form.Item name={"device_parameter"} label="Parameter(Device-Level)" initialValue={[]}>
                  <Select
                    placeholder="Select"
                    mode="multiple"
                    maxTagCount="responsive"
                    dropdownRender={(menu) =>
                      SelectorFooter({
                        menu,
                        selectAll: () =>
                          form.setFieldsValue({
                            device_parameter: deviceOptions.map((o) => o.value),
                          }),
                        clearAll: () =>
                          form.setFieldsValue({
                            device_parameter: [],
                          }),
                      })
                    }
                  >
                    {deviceOptions.map((opt) => (
                      <Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Fragment>

            <Col xs={24} sm={24} md={12} lg={12} xl={6} xxl={5}>
              <Form.Item
                name={"duration"}
                label="Period"
                initialValue={[moment().startOf("day"), moment().endOf("day")]}
              >
                <RangePicker
                  ranges={{
                    Today: [moment().startOf("day"), moment().endOf("day")],
                    Yesterday: [moment().startOf("day").subtract(1, "days"), moment().endOf("day").subtract(1, "days")],
                    "Last 7 days": [
                      moment().startOf("day").subtract(6, "days"),
                      moment().subtract(0, "days").endOf("day"),
                    ],
                    "Last 30 days": [
                      moment().startOf("day").subtract(0, "months").subtract(29, "days"),
                      moment().endOf("day"),
                    ],
                  }}
                  value={hackValue || value}
                  disabledDate={disabledStartDate}
                  format="YYYY-MM-DD"
                  showTime={false}
                  style={{ width: "100%" }}
                  onChange={(value) => dateOnChange(value)}
                  onOpenChange={onOpenChange}
                  onCalendarChange={(val) => {
                    setDates(val);
                  }}
                />
              </Form.Item>
            </Col>
            {/* )} */}
            <Col xs={24} sm={24} md={12} lg={12} xl={6} xxl={3}>
              <Form.Item name={"unit"} label="Unit" initialValue={time[0].value}>
                <Select
                  //@ts-ignore
                  value={unit}
                  onChange={onUnitChange}
                >
                  {time.map((opt: any) => {
                    return (
                      <Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>

            <Col flex="1">
              <Row justify="end">
                <Button
                  style={{ marginBottom: "16px" }}
                  type="primary"
                  htmlType="submit"
                  onClick={() => setCurrentTime(Math.round(Date.now() / 1000))}
                >
                  {t("ISMS.Run")}
                </Button>
              </Row>
            </Col>
          </Row>
        </div>
      </Form>
    </Card>
  );
};

export default OnDemandFilter;
